import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { authEvents } from '@/api/auth-events';
import { portalLogout } from '@/api/generated';
import { organizationScope } from '@/api/organization-scope';
import { tokenStore } from '@/api/token-store';

import { AuthContext, type AuthContextValue, type CurrentUser } from './auth-context';

const SESSION_KEY = 'activistarc.portal.user';

function readStoredUser(): CurrentUser | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
}

/**
 * The signed-in person, derived from the stored session.
 *
 * The user is kept alongside the tokens rather than decoded from them, because
 * what the portal needs to show - a name, an organization - is not all in the
 * token, and decoding a token to trust its contents client-side is the wrong
 * habit to build.
 *
 * The role is real for all three kinds of account now, and it is the role held
 * in the organization currently open. Somebody may be a Super Admin of one
 * organization and an Admin of another, so switching organization switches
 * their role with it.
 *
 * VITE_DEV_ROLE used to stand in for organization Super Admins and Admins,
 * who had no accounts on the backend. They have them now, so that fallback is
 * gone - it would have overridden a real Super Admin's role with whatever the
 * developer happened to have set.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // Reading the stored session is synchronous, so it is done as the initial
  // state rather than in an effect. Doing it in an effect would render once
  // signed out and then again signed in, which flashes the sign-in screen.
  // Only trust a stored user if the tokens are still there too.
  const [user, setUser] = useState<CurrentUser | null>(() =>
    tokenStore.get() ? readStoredUser() : null,
  );

  // Kept in the contract because validating the session against the server will
  // be asynchronous once portal accounts exist.
  const loading = false;

  const signIn = useCallback((next: CurrentUser) => {
    // Every sign-in knows its own role, whichever of the three it is. Nothing
    // is substituted here any more.
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    // The API layer reads this outside React, to put the organization on every
    // portal request. Set together with the session so the two cannot drift.
    organizationScope.set(next.organizationId);
    setUser(next);
  }, []);

  const switchOrganization = useCallback((organizationId: string) => {
    setUser((current) => {
      if (!current) return current;
      const chosen = current.organizations.find((o) => o.id === organizationId);
      // An id they do not belong to is ignored. The backend would refuse it
      // anyway, but changing the visible organization to one they cannot read
      // would leave every page showing a not-found.
      if (!chosen) return current;

      const next: CurrentUser = {
        ...current,
        organizationId: chosen.id,
        organizationName: chosen.name,
        // Their role travels with the organization, so this is the whole
        // reason switching is more than a label change.
        role: chosen.role,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
      organizationScope.set(next.organizationId);
      return next;
    });
  }, []);

  const signOut = useCallback(() => {
    /*
     * Clear what this browser holds first, so pressing Log Out always signs the
     * person out here whatever the network does. Then end the session on the
     * server, using the token captured before it was cleared.
     *
     * That capture is the point. The request used to be started without being
     * waited for and the tokens thrown away on the very next line - but the
     * generated client pauses at an `await` before it reads the token, so by
     * the time it looked there was nothing there. The request went out with no
     * identification, the server answered 401, and the session stayed alive.
     * `catch(() => undefined)` hid it. Anybody holding a copy of the token
     * could carry on using it, which is the opposite of what signing out means.
     */
    const tokens = tokenStore.get();

    sessionStorage.removeItem(SESSION_KEY);
    tokenStore.clear();
    organizationScope.clear();
    setUser(null);
    // Tells the query cache to drop everything, so one person's data is never
    // left on screen after another signs in.
    authEvents.emitSignedOut('manual');

    if (!tokens) return;
    void portalLogout({
      headers: { Authorization: `Bearer ${tokens.accessToken}` },
    }).catch(() => undefined);
  }, []);

  useEffect(
    () =>
      // The API layer signs the person out when the session is beyond saving.
      authEvents.onSignedOut(() => {
        sessionStorage.removeItem(SESSION_KEY);
        organizationScope.clear();
        setUser(null);
      }),
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, signIn, switchOrganization, signOut }),
    [user, loading, signIn, switchOrganization, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
